import React from 'react';
import {
    AppBar as MuiAppBar,
    Toolbar,
    Button,
    Box,
    Switch,
    IconButton,
    useMediaQuery,
    useTheme,
    Menu,
    MenuItem
} from '@mui/material';
import {
    Home as HomeIcon,
    Login as LoginIcon,
    PersonAdd as PersonAddIcon,
    Menu as MenuIcon,
    Info as InfoIcon,
    ContactMail as ContactIcon,
    School as LearnIcon,
    Payments as PaymentsIcon,
    Event as EventIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LogoDisplay from './LogoDisplay';

const Navbar = ({ toggleDarkMode, darkMode }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallDesktop = useMediaQuery(theme.breakpoints.between('md', 'lg'));
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNavigation = (path) => {
        navigate(path);
        handleClose();
    };

    const navLinks = [
        { path: '/', label: 'Home', icon: <HomeIcon /> },
        { path: '/fundraise-campaigns', label: 'Fundraisers', icon: <PaymentsIcon /> },
        { path: '/events', label: 'Events', icon: <EventIcon /> },
        { path: '/about-us', label: 'About Us', icon: <InfoIcon /> },
        { path: '/contact-us', label: 'Contact Us', icon: <ContactIcon /> }
    ];

    const authLinks = [
        { path: '/login', label: 'Login', icon: <LoginIcon /> },
        { path: '/signup', label: 'Sign Up', icon: <PersonAddIcon /> }
    ];

    // Custom button style
    const buttonStyle = {
        textTransform: 'none',
        fontSize: '0.875rem',
        fontWeight: 400,
        px: 1.5,
        minWidth: 'auto',
        color: 'inherit'
    };

    return (
        <MuiAppBar position="static" sx={{ py: { xs: 0.5, sm: 0 } }}>
            <Toolbar sx={{
                justifyContent: 'space-between',
                px: { xs: 1, sm: 2, md: 3 },
                minHeight: { xs: '56px', sm: '64px' } // Reduced height for mobile
            }}>
                {/* Left side - Logo */}
                <Box sx={{ display: 'flex', alignItems: 'center' }} >
                    <LogoDisplay />

                    {/* Show nav links on tablet (md) but not mobile */}
                    {!isMobile && (
                        <Box sx={{
                            display: { xs: 'none', md: 'flex' },
                            gap: { md: 0.5, lg: 1 },
                            ml: { md: 1, lg: 2 }
                        }}>
                            {navLinks.slice(0, isSmallDesktop ? 2 : 3).map((link) => (
                                <Button
                                    key={link.path}
                                    onClick={() => navigate(link.path)}
                                    size="small"
                                    sx={{
                                        ...buttonStyle,
                                        fontSize: { md: '0.8rem', lg: '0.875rem' }
                                    }}
                                >
                                    {link.label}
                                </Button>
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Right side - Auth Links and Dark Mode Toggle */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 0.5, sm: 1, md: 1.5 }
                }}>
                    {/* Desktop view - show all links and auth buttons */}
                    {!isMobile && (
                        <>
                            <Box sx={{
                                display: { xs: 'none', lg: 'flex' },
                                gap: 1
                            }}>
                                {navLinks.slice(isSmallDesktop ? 2 : 3).map((link) => (
                                    <Button
                                        key={link.path}
                                        onClick={() => navigate(link.path)}
                                        size="small"
                                        sx={buttonStyle}
                                    >
                                        {link.label}
                                    </Button>
                                ))}
                            </Box>

                            {authLinks.map((link) => (
                                <Button
                                    key={link.path}
                                    startIcon={React.cloneElement(link.icon, {
                                        sx: { fontSize: { md: '1.1rem', lg: '1.2rem' } }
                                    })}
                                    onClick={() => navigate(link.path)}
                                    size="small"
                                    sx={{
                                        ...buttonStyle,
                                        fontSize: { md: '0.8rem', lg: '0.875rem' }
                                    }}
                                >
                                    {link.label}
                                </Button>
                            ))}
                            <Switch
                                checked={darkMode}
                                onChange={toggleDarkMode}
                                color="secondary"
                                size="small"
                                inputProps={{ 'aria-label': 'toggle dark mode' }}
                            />
                        </>
                    )}

                    {/* Mobile/tablet view - show menu button */}
                    {isMobile && (
                        <>
                            <IconButton
                                size="medium"
                                edge="end"
                                color="inherit"
                                aria-label="menu"
                                onClick={handleMenu}
                                sx={{ p: 0.75, mr: 1.0 }}
                            >
                                <MenuIcon fontSize="large" />
                            </IconButton>
                            <Menu
                                id="menu-appbar"
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                open={open}
                                onClose={handleClose}
                                sx={{
                                    '& .MuiPaper-root': {
                                        minWidth: 180,
                                        mt: 0.5,
                                        mr: 0.5,
                                        ml: 2.0,
                                        marginTop: {
                                            xs: '48px',
                                        }
                                    }
                                }}
                            >
                                {navLinks.map((link) => (
                                    <MenuItem
                                        key={link.path}
                                        onClick={() => handleNavigation(link.path)}
                                        sx={{ py: 0.75 }}
                                    >
                                        {React.cloneElement(link.icon, {
                                            sx: { mr: 1.5, fontSize: '1.1rem' }
                                        })}
                                        {link.label}
                                    </MenuItem>
                                ))}
                                <Box sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)', my: 0.5 }} />
                                {authLinks.map((link) => (
                                    <MenuItem
                                        key={link.path}
                                        onClick={() => handleNavigation(link.path)}
                                        sx={{ py: 0.75 }}
                                    >
                                        {React.cloneElement(link.icon, {
                                            sx: { mr: 1.5, fontSize: '1.1rem' }
                                        })}
                                        {link.label}
                                    </MenuItem>
                                ))}
                                <MenuItem

                                >
                                    <Switch
                                        checked={darkMode}
                                        onChange={toggleDarkMode}
                                        color="secondary"
                                        size="small"
                                        inputProps={{ 'aria-label': 'toggle dark mode' }}
                                        sx={{ mr: 0.5 }}
                                    /> Dark/Light Mode
                                </MenuItem>


                            </Menu>
                        </>
                    )}
                </Box>
            </Toolbar>
        </MuiAppBar>
    );
};

export default Navbar;